// @ts-nocheck
import { forwardRef } from 'react';
import {
  Box,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { Card } from '../../../ui';
import { FadeIn } from '../../../animations';
import type { CalendarHeatmapProps } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * CalendarHeatmap - GitHub-style calendar heatmap
 *
 * Displays activity data over a year in a calendar format.
 */
const CalendarHeatmap = forwardRef<HTMLDivElement, CalendarHeatmapProps>(
  (
    {
      title,
      subtitle,
      data,
      startDate,
      endDate,
      colorScale = ['#ebedf0', '#9be9a8', '#40c463', '#30a14e', '#216e39'],
      tooltipFormatter,
      cellSize = 12,
      cellGap = 3,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Helper to format date as YYYY-MM-DD
    const formatDate = (date: Date) => {
      return date.toISOString().split('T')[0];
    };

    // Create a map of dates to counts
    const dataMap = new Map(
      data.map((d) => [
        typeof d.date === 'string' ? d.date : formatDate(new Date(d.date)),
        d.count,
      ])
    );

    // Find min/max counts
    const counts = data.map((d) => d.count);
    const minCount = Math.min(...counts, 0);
    const maxCount = Math.max(...counts);

    const getColor = (count: number) => {
      if (count === 0) return colorScale[0];
      if (maxCount === minCount) return colorScale[Math.floor(colorScale.length / 2)];
      const normalized = (count - minCount) / (maxCount - minCount);
      const index = Math.ceil(normalized * (colorScale.length - 2)) + 1;
      return colorScale[Math.min(index, colorScale.length - 1)];
    };

    // Generate weeks
    const weeks: Date[][] = [];
    let currentWeek: Date[] = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    // Start from Sunday
    while (current.getDay() !== 0) {
      current.setDate(current.getDate() - 1);
    }

    while (current <= end) {
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      currentWeek.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <FadeIn>
        <Card ref={ref} withBorder p="lg" className={className}>
          <Stack gap="md">
            <Box>
              {title && <Text size="lg" fw={600} style={accessibilityStyles}>{title}</Text>}
              {subtitle && <Text size="sm" c="dimmed" style={accessibilityStyles}>{subtitle}</Text>}
            </Box>

            <Box style={{ overflowX: 'auto' }}>
              <Box style={{ display: 'inline-flex', gap: cellGap }}>
                {/* Day labels */}
                <Stack gap={cellGap} style={{ paddingTop: 15 }}>
                  {days.map((day, idx) => (
                    <Box
                      key={day}
                      style={{
                        height: cellSize,
                        display: idx % 2 === 1 ? 'flex' : 'none',
                        alignItems: 'center',
                        paddingRight: 4,
                      }}
                    >
                      <Text size="10px" c="dimmed">{day}</Text>
                    </Box>
                  ))}
                </Stack>

                {/* Weeks */}
                <Box>
                  <Box style={{ display: 'flex', gap: cellGap }}>
                    {weeks.map((week, weekIdx) => (
                      <Stack key={weekIdx} gap={cellGap}>
                        {week.map((date, dayIdx) => {
                          const dateStr = formatDate(date);
                          const count = dataMap.get(dateStr) || 0;
                          const color = getColor(count);

                          return (
                            <Tooltip
                              key={dateStr}
                              label={
                                tooltipFormatter
                                  ? tooltipFormatter({ date: dateStr, count })
                                  : `${dateStr}: ${count} activities`
                              }
                              withArrow
                            >
                              <Box
                                style={{
                                  width: cellSize,
                                  height: cellSize,
                                  backgroundColor: color,
                                  borderRadius: 2,
                                  cursor: 'pointer',
                                }}
                              />
                            </Tooltip>
                          );
                        })}
                      </Stack>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text size="xs" c="dimmed" style={accessibilityStyles}>Less</Text>
              <Box style={{ display: 'flex', gap: 2 }}>
                {colorScale.map((color, idx) => (
                  <Box
                    key={idx}
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: color,
                      borderRadius: 2,
                    }}
                  />
                ))}
              </Box>
              <Text size="xs" c="dimmed" style={accessibilityStyles}>More</Text>
            </Box>
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

CalendarHeatmap.displayName = 'CalendarHeatmap';

export default CalendarHeatmap;
